import { t } from "@repo/shared/i18n";
import { useCallback } from "react";
import { isMobileSafari } from "react-device-detect";
import { useDispatch } from "react-redux";

import { sendMessageToExtension } from "../functions/sendMessageToExtension";
import { syncExtensionStatusFromResponse } from "../redux/clubImport/clubImportSlice";
import { addAlert, showModal } from "../redux/message/messageSlice";
import { AppDispatch } from "../redux/store";
import { User } from "../redux/user/userSlice";

type UseInitImportProps = {
  user: User;
};

// This function should be the "brain" behind import, so that we can reuse it easier in other places
export const useInitImport = ({ user }: UseInitImportProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const fromExtensioniOSApp = !!localStorage.getItem("FROM_EXTENSION_APP");

  const initImport = useCallback(async () => {
    // Free, but still an upload against an account — an anonymous run has every
    // batch rejected.
    if (!user?.uuid) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "import-club",
        }),
      );
      return Promise.reject(new Error("User must be logged in to import."));
    }

    if (!window.chrome && !isMobileSafari) {
      dispatch(
        addAlert({
          type: "error",
          title: t("clubImport.alertTitle"),
          description: t("clubImport.chromeRequired"),
        }),
      );
      return Promise.reject(new Error("Google Chrome is required."));
    } else if (isMobileSafari && !fromExtensioniOSApp) {
      dispatch(
        showModal({
          modalType: "ios-import-popup",
        }),
      );
      return Promise.reject(new Error("Safari extension is required."));
    }
    try {
      const res = await sendMessageToExtension({
        user,
        safariMobile: isMobileSafari,
        startRequest: true,
      });
      dispatch(syncExtensionStatusFromResponse(res));
    } catch (error) {
      dispatch(
        addAlert({
          type: "error",
          title: t("clubImport.alertTitle"),
          description:
            error instanceof Error ? error.message : t("error.unexpected"),
        }),
      );
      return Promise.reject(new Error("Chrome Extension is required."));
    }
  }, [dispatch, fromExtensioniOSApp, user]);

  return initImport;
};
